import React, { useEffect, useMemo, useRef, useState } from "react";
import { Gift } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";

/**
 * Premium Spin Wheel with SVG segments and CSS rotation animation.
 * The wheel rotates via a single cumulative `rotation` state, so the
 * CSS transition always sees a delta and plays smoothly.
 *
 * Props
 * - segments             — array of { label, color }
 * - spinning             — boolean
 * - resultIndex          — index of the winning segment (or null)
 * - onSpin               — click handler on the center button
 * - disabled             — disable the center button
 * - labelOrientation     — "horizontal" (default) or "vertical" (radial)
 * - preview              — when true, render in compact preview mode
 *                          (no center spin button, no glow, smaller hub).
 *                          Used inside the game editor next to the
 *                          orientation Select for live preview.
 */

/**
 * Wrap a label into 1 or 2 lines depending on orientation + length.
 * Returns a list of strings (1 or 2 items). Truncates each line with `…`
 * if it overflows the per-line max.
 *
 * Horizontal:
 *   - <= 10 chars OR no space    → 1 line, truncate at 14 chars
 *   - otherwise                  → 2 lines, split on the space closest
 *                                  to the middle, each line ≤ 13 chars
 * Vertical:
 *   - always 1 line, truncate at 22 chars
 */
export function wrapLabel(label, isVertical) {
  const text = (label || "").trim();
  if (!text) return [""];

  if (isVertical) {
    const max = 22;
    return [text.length > max ? text.slice(0, max - 1) + "…" : text];
  }

  // Horizontal
  const singleMax = 14;
  if (text.length <= 10 || !text.includes(" ")) {
    return [text.length > singleMax ? text.slice(0, singleMax - 1) + "…" : text];
  }

  // Multi-word, long enough: split into 2 balanced lines.
  const words = text.split(/\s+/);
  let best = {
    line1: words[0],
    line2: words.slice(1).join(" "),
    diff: Infinity,
  };
  for (let i = 1; i < words.length; i++) {
    const l1 = words.slice(0, i).join(" ");
    const l2 = words.slice(i).join(" ");
    const d = Math.abs(l1.length - l2.length);
    if (d < best.diff) best = { line1: l1, line2: l2, diff: d };
  }
  const lineMax = 13;
  const line1 =
    best.line1.length > lineMax ? best.line1.slice(0, lineMax - 1) + "…" : best.line1;
  const line2 =
    best.line2.length > lineMax ? best.line2.slice(0, lineMax - 1) + "…" : best.line2;
  return [line1, line2];
}

export default function SpinWheel({
  segments = [],
  spinning = false,
  resultIndex = null,
  onSpin,
  disabled = false,
  labelOrientation = "horizontal",
  preview = false,
  labelFontFamily = null,
  labelFontWeight = null,
  labelFontScale = 1,
  hubLogo = null,
  showPrizeNames = true,
}) {
  const count = segments.length || 8;
  const [rotation, setRotation] = useState(0);
  const appliedForResult = useRef(null);
  const isVertical = labelOrientation === "vertical";

  const { paths, labels } = useMemo(() => {
    const cx = 200;
    const cy = 200;
    const r = 195;
    const per = 360 / count;
    const paths = [];
    const labels = [];
    for (let i = 0; i < count; i++) {
      const a1 = (i * per - 90) * (Math.PI / 180);
      const a2 = ((i + 1) * per - 90) * (Math.PI / 180);
      const x1 = cx + r * Math.cos(a1);
      const y1 = cy + r * Math.sin(a1);
      const x2 = cx + r * Math.cos(a2);
      const y2 = cy + r * Math.sin(a2);
      const large = per > 180 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      paths.push({ d, color: segments[i]?.color || "#E2E8F0" });
      const mid = ((i + 0.5) * per - 90) * (Math.PI / 180);
      // Vertical labels sit a bit further out so longer strings still fit
      // inside the segment without touching the hub.
      const tr = isVertical ? r * 0.55 : r * 0.62;
      const rawLabel = segments[i]?.label || "";
      const lines = wrapLabel(rawLabel, isVertical);
      // Prize picture (replaces the label): sits mid-radius, points to the rim,
      // sized to the chord available at that radius.
      const ir = r * 0.64;
      const chord = 2 * ir * Math.sin((per * Math.PI) / 360);
      labels.push({
        x: cx + tr * Math.cos(mid),
        y: cy + tr * Math.sin(mid),
        image: resolveImageUrl(segments[i]?.image_url),
        // Names hidden: winning segments without a photo get a gift icon.
        gift: !showPrizeNames && !!segments[i]?.is_winning,
        ix: cx + ir * Math.cos(mid),
        iy: cy + ir * Math.sin(mid),
        imgSize: Math.min(chord * 0.92, r * 0.5),
        imgAngle: (i + 0.5) * per,
        // Vertical: rotate so the text runs along the radial line, with
        // top of letters pointing toward the wheel rim (readable when the
        // segment is at the top; tilt head for side segments).
        // Horizontal (default): tangent to the wheel circle.
        angle: isVertical
          ? (i + 0.5) * per - 90
          : (i + 0.5) * per,
        lines,
      });
    }
    return { paths, labels };
  }, [segments, count, isVertical, showPrizeNames]);

  // When we receive a new result while spinning, compute the additional rotation
  // so that the center of resultIndex lines up with the pointer at the top.
  useEffect(() => {
    if (
      spinning &&
      resultIndex !== null &&
      appliedForResult.current !== resultIndex + "-" + spinning
    ) {
      const per = 360 / count;
      const targetAngle = resultIndex * per + per / 2; // 0deg = up (top), clockwise
      // Current visual angle (modulo 360) of the top segment
      const currentMod = ((rotation % 360) + 360) % 360;
      // We need: final rotation mod 360 = 360 - targetAngle
      const wantedMod = (360 - targetAngle + 360) % 360;
      const delta = (wantedMod - currentMod + 360) % 360;
      const addition = 6 * 360 + delta; // 6 full spins + alignment
      appliedForResult.current = resultIndex + "-" + spinning;
      setRotation((r) => r + addition);
    }
  }, [spinning, resultIndex, count, rotation]);

  // When a new round starts (spinning reset to false and no result), clear the guard
  useEffect(() => {
    if (!spinning && resultIndex === null) {
      appliedForResult.current = null;
    }
  }, [spinning, resultIndex]);

  // Font size — slightly smaller when 2 lines are rendered to give them
  // breathing room vertically inside the segment.
  const baseFontSize = isVertical
    ? Math.max(11, 20 - Math.floor(count / 2))
    : Math.max(10, 22 - count);

  return (
    <div
      className={
        preview
          ? "relative w-full mx-auto aspect-square select-none"
          : "relative w-full max-w-[420px] mx-auto aspect-square select-none"
      }
    >
      {/* Glow — hidden in preview to keep the editor card clean */}
      {!preview && (
        <div className="absolute inset-4 rounded-full qrise-gradient opacity-30 blur-2xl" />
      )}

      {/* Pointer */}
      <div
        data-testid="spin-wheel-pointer"
        className="absolute top-0 left-1/2 -translate-x-1/2 z-20"
        style={{
          transform: preview ? "translate(-50%, -30%) scale(0.7)" : "translate(-50%, -25%)",
        }}
      >
        <svg width="34" height="44" viewBox="0 0 34 44">
          <defs>
            <filter id="ps" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.25" />
            </filter>
          </defs>
          <path
            d="M17 44 L2 10 Q17 -4 32 10 Z"
            fill="hsl(var(--foreground))"
            filter="url(#ps)"
          />
          <circle cx="17" cy="12" r="3" fill="hsl(var(--background))" />
        </svg>
      </div>

      {/* Wheel */}
      <div
        className={
          preview
            ? "absolute inset-0 rounded-full overflow-hidden border-2 border-white dark:border-zinc-900 shadow-md"
            : "absolute inset-0 rounded-full overflow-hidden border-[6px] border-white dark:border-zinc-900 shadow-[0_20px_60px_-10px_rgba(139,92,246,0.35)]"
        }
        data-testid="spin-wheel-disc"
      >
        <svg
          viewBox="0 0 400 400"
          className="w-full h-full"
          style={{
            transformOrigin: "center",
            transform: `rotate(${rotation}deg)`,
            transition: preview
              ? "none"
              : "transform 5s cubic-bezier(0.22, 0.9, 0.25, 1)",
            willChange: "transform",
          }}
        >
          {paths.map((p, i) => (
            <path
              key={i}
              d={p.d}
              fill={p.color}
              stroke="#ffffff"
              strokeWidth="1.5"
            />
          ))}
          {labels.map((l, i) =>
            l.image ? (
              <g
                key={i}
                transform={`rotate(${l.imgAngle} ${l.ix} ${l.iy})`}
                style={{ pointerEvents: "none" }}
              >
                <image
                  href={l.image}
                  x={l.ix - l.imgSize / 2}
                  y={l.iy - l.imgSize / 2}
                  width={l.imgSize}
                  height={l.imgSize}
                  preserveAspectRatio="xMidYMid meet"
                  data-testid={`spin-wheel-image-${i}`}
                />
              </g>
            ) : l.gift ? (
              <g key={i} transform={`rotate(${l.imgAngle} ${l.ix} ${l.iy})`} style={{ pointerEvents: "none" }}>
                <Gift
                  x={l.ix - (l.imgSize * 0.7) / 2}
                  y={l.iy - (l.imgSize * 0.7) / 2}
                  width={l.imgSize * 0.7}
                  height={l.imgSize * 0.7}
                  color="#0f172a"
                  strokeWidth={2}
                  data-testid={`spin-wheel-gift-${i}`}
                />
              </g>
            ) : (
            <g
              key={i}
              transform={`rotate(${l.angle} ${l.x} ${l.y})`}
              style={{ pointerEvents: "none" }}
            >
              <text
                x={l.x}
                y={l.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily={labelFontFamily || "Outfit, sans-serif"}
                fontWeight={labelFontWeight || "700"}
                fontSize={
                  (l.lines.length === 2 ? baseFontSize - 1 : baseFontSize) *
                  labelFontScale
                }
                fill="#0f172a"
                style={{ letterSpacing: "-0.01em" }}
              >
                {l.lines.length === 1 ? (
                  l.lines[0]
                ) : (
                  <>
                    <tspan x={l.x} dy="-0.55em">{l.lines[0]}</tspan>
                    <tspan x={l.x} dy="1.1em">{l.lines[1]}</tspan>
                  </>
                )}
              </text>
            </g>
            )
          )}
        </svg>
      </div>

      {/* Center hub */}
      {preview ? (
        <div
          aria-hidden
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white dark:bg-zinc-900 border-2 border-white dark:border-zinc-900 shadow"
          style={{
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
          }}
        />
      ) : (
        <button
          type="button"
          onClick={onSpin}
          disabled={disabled || spinning}
          data-testid="spin-wheel-btn"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-24 w-24 rounded-full bg-white dark:bg-zinc-900 border-4 border-white dark:border-zinc-900 shadow-xl flex items-center justify-center font-semibold tracking-tight text-sm disabled:opacity-70 hover:scale-105 active:scale-95 transition-transform"
          style={{
            boxShadow:
              "0 10px 30px rgba(139,92,246,0.35), inset 0 0 0 1px rgba(0,0,0,0.06)",
          }}
        >
          {hubLogo ? (
            <img
              src={hubLogo}
              alt="logo"
              className="h-16 w-16 rounded-full object-cover pointer-events-none"
              data-testid="spin-wheel-hub-logo"
            />
          ) : (
            <span className="qrise-text-gradient text-base font-bold">
              {spinning ? "…" : "SPIN"}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
